import { SmartFilter } from '@Portal/models/smartFilters/SmartFilter';
import { OrderByOperator } from './OrderByOperator';

export interface TemplateField {
  key: string;
  friendlyName: string;
  value: string;
  typeName: TypeName;
  isCollection: boolean;
  isCleaned: boolean;
  localeTitle: string;
}

export enum ProcessflowActionType {
  TransitionAction = 'TransitionAction',
  AllocationAction = 'AllocationAction',
  SendEmailAction = 'SendEmailAction',
  // @NOTE: Not yet implemented
  TagAction = 'TagAction',
}

export interface ProcessFlowObjectDetail {
  processFlowTemplateObject: ProcessFlowCard;
  view: {
    detailTemplate: string;
    smartDataModuleId: string;
  };
  // there are many other fields but they aren't useful to Vue portal since the template object
  // contains all the info.
}

export interface ProcessflowActionHistory {
  actionFriendlyName: string;
  actionHistoryDescription: string;
  actionHistoryIconClass: string;
  actionName: string;
  actionType: ProcessflowActionType;
  actionedByUserId: string;
  actionedByUserName: string;
  createdAt: string;
  fromState: string;
  metadata: {
    [key: string]: string;
  };
  processflowActionHistoryId: number;
  processflowObjectId: number;
  properties: Record<string, unknown>;
  toState?: string;
  toSubState?: string;
}

export interface ProcessflowActionResponse {
  actionHistoryRecord: ProcessflowActionHistory;
  permittedActionGroups: ActionGroup[];
  processFlowObject: { state: string; subState: string };
}

export interface ProcessflowActionHistoryResponse {
  history: ProcessflowActionHistory[];
}

export enum TypeName {
  String,
  Number,
  DateTime,
}

export interface TemplateMetricField {
  friendlyName: string;
  index?: number;
  label: string;
  value: number | string;
  localeTitle: string;
}

export interface Action {
  name: string;
  friendlyName: string;
  actionType: string;
  actionGroup: string;
  iconClass: string;
  isSocial: boolean;
  weight: number;
  emailSender?: string;
  quickTemplates?: { [name: string]: string };
}

export interface ActionGroup {
  name: string;
  actionType: ProcessflowActionType;
  iconClass: string;
  weight: number;
  showInActionBar: boolean;
  actionItems: Action[];
}

export interface SmartDataTemplateObject {
  id: string;
  program: string;
  primaryMetric: TemplateMetricField;
  summaryVerbatim?: TemplateField;
  metrics: TemplateMetricField[];
  drivers: TemplateField[];
  responded: TemplateField[];
  title: TemplateField[];
  location: TemplateField[];
  headerTop: TemplateField[];
  headerBottom: TemplateField[];
  footer: TemplateField[];
  timeline: TemplateField[];
  contact: {
    customerName?: TemplateField;
    title: TemplateField[];
    email?: TemplateField;
    mobile?: TemplateField;
    other: TemplateField[];
  };
  verbatimQuestions: TemplateField[];
  linkQuestions?: TemplateField[];
  driverQuestions: TemplateField[];
  metricQuestions: TemplateMetricField[];
  details: TemplateField[];
  categorisedDetails: Record<string, TemplateField[]>;
  facets: Record<string, string | null>;
}

/*
  Versioning is hard, so the point is for this to be as "general" as reasonable
  to reduce the pressure in future for changes.
  This covers both detail & summary requirements does mean that we want to ensure
  we only load cards that are relevant (i.e. top 20).
*/
export interface ProcessFlowCard extends SmartDataTemplateObject {
  processFlow: {
    processFlowObjectId: number | undefined;
    isTerminatingState: boolean;
    state: string;
    subState: string;
    actionGroups: ActionGroup[];
    history?: ProcessflowActionHistory[];
  };
}

export interface ProcessFlowQueue {
  view: {
    name: string;
    type: number;
    summaryTemplate: string;
    recordsPerPage: number;
    isDefault: boolean;
  };
  hasExport: boolean;
  tag?: ProcessFlowQueueTag;
  smartDataModuleId: string;
  name: string;
  defaultOrderBy: {
    fieldKey: string;
    operator: number;
    operatorAsString: OrderByOperator;
  };
  defaultSmartFilters?: SmartFilter[];
  smartDataTags: string[];
  smartFilterTags: string[];
  favouritesModuleId: string;
  isSocial: boolean;
  dateFilterType: string;
  ignoreClaimsForFilters: boolean;
}

export interface ProcessFlowQueueCount {
  name: string;
  tag: ProcessFlowQueueTag;
  count: number;
}

export enum ProcessFlowQueueTag {
  Initial = 'Initial',
  InProgress = 'InProgress',
  Final = 'Final',
  All = 'All',
}
